import { Intent } from "@blueprintjs/core"
import { useMutation, useQuery } from "@tanstack/react-query"
import React, { useCallback, useMemo, useState } from "react"

import {
  ControlThresholds,
  CorrectionEntry,
  MatchStatus,
  NavControlResult,
  NavSubmissionCreateInput,
  NavSubmissionRow,
  NavSubmissionRowSide,
  PositionDiff,
  ReconciliationResult,
  ShareClassNavResult,
  ShareCountControlResult,
} from "../../../../api/reconciliation"
import { useApi } from "../../../hooks/useApi"
import { toaster } from "../../../utils/createToaster"
import {
  applyOverride,
  buildCorrectionHistoryMap,
  buildShareClassOverrides,
  buildShareCountAcceptances,
  effectiveShareClasses,
  isPriceGapBreach,
  operatorNavControls,
  operatorPriceGapControl,
  PriceGapBreaches,
  RowOverride,
  ShareClassEdit,
  ShareCountAcceptanceEdit,
  unacceptedShareCountBreaches,
} from "./reconciliation.utils"

// The grid's row filter: a match status, every row, or the rows whose
// price-gap control did not pass (CONTEXT.md, "Breach") — the filter that
// brings the breached rows up out of the collapsed groups.
export type StatusFilter = MatchStatus | "all" | "price_gap_breaches"

const numToStr = (v: number | null | undefined): string | null =>
  v == null ? null : String(v)

const buildSide = (
  price: number | null,
  quantity: number | null,
  market_value: number | null,
): NavSubmissionRowSide | null => {
  if (price == null && quantity == null && market_value == null) return null
  return {
    price: numToStr(price),
    quantity: numToStr(quantity),
    market_value: numToStr(market_value),
  }
}

const toSubmissionRow = (
  effective: PositionDiff,
  override: RowOverride | undefined,
  baseline: PositionDiff,
): NavSubmissionRow => {
  const edited =
    override !== undefined &&
    (override.override_price !== undefined ||
      override.override_quantity !== undefined ||
      override.override_market_value !== undefined)
  return {
    identifier: effective.identifier,
    description: effective.description,
    currency: effective.currency,
    asset_type: effective.asset_type,
    status: effective.match_status,
    scient: buildSide(effective.price_a, effective.quantity_a, effective.market_value_a),
    caceis: buildSide(effective.price_b, effective.quantity_b, effective.market_value_b),
    diff: buildSide(effective.price_diff, effective.quantity_diff, effective.market_value_diff),
    original_scient: edited
      ? buildSide(baseline.price_a, baseline.quantity_a, baseline.market_value_a)
      : null,
    overrides: edited
      ? {
          price: numToStr(override?.override_price ?? null),
          quantity: numToStr(override?.override_quantity ?? null),
          market_value: numToStr(override?.override_market_value ?? null),
        }
      : null,
    comment: override?.comment ?? "",
    multiplication_factor: numToStr(effective.multiplication_factor),
    fx_rate: numToStr(effective.fx_rate),
    // The unit prices in fund currency as the operator saw them — the
    // Scient one recomputed through the row's valuation when corrected —
    // so the server re-decides the price-gap control on the same figures.
    unit_price_a: numToStr(effective.unit_price_a),
    unit_price_b: numToStr(effective.unit_price_b),
  }
}

export const useReconciliation = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [sourceType, setSourceType] = useState("caceis")
  const [result, setResult] = useState<ReconciliationResult | null>(null)
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all")
  const [edits, setEdits] = useState<Map<string, RowOverride>>(new Map())
  // Edited shares outstanding per share class (#1631), keyed by class code.
  const [shareClassEdits, setShareClassEdits] = useState<Map<string, ShareClassEdit>>(
    new Map(),
  )
  // Share-count acceptances pending submit (CONTEXT.md, "Share-count
  // acceptance"), keyed by class code: the operator took the file's count.
  const [shareCountAcceptances, setShareCountAcceptances] = useState<
    Map<string, ShareCountAcceptanceEdit>
  >(new Map())

  const { scientApi } = useApi()

  const compareMutation = useMutation({
    mutationFn: (file: File) => scientApi.reconciliation.compare(file, sourceType),
    onSuccess: (data: ReconciliationResult) => {
      setResult(data)
      setStatusFilter("all")
      setEdits(new Map())
      setShareClassEdits(new Map())
      setShareCountAcceptances(new Map())
      toaster.show({
        message: `Reconciliation complete: ${data.count_paired} paired, ${
          data.count_a_only + data.count_b_only
        } unmatched`,
        intent: Intent.SUCCESS,
      })
    },
    onError: (error: any) => {
      toaster.show({
        message: error.response?.data?.error || "Reconciliation failed. Please try again.",
        intent: Intent.DANGER,
      })
    },
  })

  // Correction audit log for the compared fund/date — refetched after each
  // submit so the history behind an edited cell is current (#1629).
  const correctionsQuery = useQuery({
    queryKey: ["reconciliation-corrections", result?.account_id, result?.nav_date],
    queryFn: () =>
      scientApi.reconciliation.listCorrections(
        result!.account_id!,
        result!.nav_date,
      ),
    enabled: result?.account_id != null,
  })

  const correctionHistory = useMemo<Map<string, CorrectionEntry[]>>(
    () => buildCorrectionHistoryMap(correctionsQuery.data?.corrections ?? []),
    [correctionsQuery.data],
  )

  const submitMutation = useMutation({
    mutationFn: (input: NavSubmissionCreateInput) =>
      scientApi.reconciliation.createSubmission(input),
    onSuccess: (data) => {
      toaster.show({
        message: `NAV submitted to ${data.s3_key}`,
        intent: Intent.SUCCESS,
      })
      setEdits(new Map())
      setShareClassEdits(new Map())
      setShareCountAcceptances(new Map())
      correctionsQuery.refetch()
    },
    onError: (error: any) => {
      toaster.show({
        message: error.response?.data?.error || "Submit failed. Please try again.",
        intent: Intent.DANGER,
      })
    },
  })

  // Attach __row_index so the grid can use the position in result.diffs as a
  // guaranteed-unique getRowId. The composite identifier|match_status|asset_type
  // key collides in real data (e.g. multiple "source_a_only|dividend|Interest
  // Income - Manual" accrual rows), and AG Grid v27 crashes inside
  // RowCtrl.updateRowIndexes with a null classList when getRowId returns
  // duplicates.
  //
  // Each row is the operator's view of it: the edit applied, and the
  // price-gap control re-evaluated by the server's rule when the edit
  // changed the row's price (operatorPriceGapControl) — the server's
  // decision stands on every other row.
  const displayDiffs = useMemo<PositionDiff[]>(() => {
    if (!result) return []
    return result.diffs.map((d, i) => {
      const override = edits.get(d.identifier)
      const enriched = operatorPriceGapControl(
        applyOverride(d, override),
        override,
        result.controls,
        result.total_market_value_b,
      )
      ;(enriched as PositionDiff & { __row_index: number }).__row_index = i
      return enriched
    })
  }, [result, edits])

  const displayTotals = useMemo(() => {
    if (!result) return null
    const sum = (key: keyof PositionDiff) =>
      displayDiffs.reduce((acc: number, d) => {
        const v = d[key] as number | null
        return acc + (v ?? 0)
      }, 0)
    const total_a = sum("market_value_a")
    const total_b = sum("market_value_b")
    // The Scient fee rows within the total: a fed fund's class NAVs read the
    // portfolio value before fees (effectiveShareClasses).
    const scient_fee_total_a = displayDiffs.reduce(
      (acc: number, d) => acc + (d.asset_type === "fee" ? d.market_value_a ?? 0 : 0),
      0,
    )
    // NAV/share recomputes via the class path (ADR 0001). Only a feedless
    // single-class fund (one class, nothing from the file) maps fund totals
    // straight onto its class; multi-class splits stay server-computed.
    const singleClass =
      result.share_class_metadata === null && result.share_classes?.length === 1
        ? result.share_classes[0]
        : null
    const singleClassShares = singleClass
      ? shareClassEdits.get(singleClass.code)?.nombre_parts ?? singleClass.nombre_parts
      : null
    return {
      total_market_value_a: total_a,
      total_market_value_b: total_b,
      total_market_value_diff: total_a - total_b,
      scient_fee_total_a,
      nav_per_share_a: singleClassShares ? total_a / singleClassShares : null,
      nav_per_share_b: singleClassShares ? total_b / singleClassShares : null,
    }
  }, [result, displayDiffs, shareClassEdits])

  // Whether any row carries a value edit (price, quantity or amount) — a
  // comment alone changes no figure. The class NAVs then follow the edited
  // totals, and the NAV control re-evaluates on them.
  const rowValuesEdited = useMemo(
    () =>
      [...edits.values()].some(
        e =>
          e.override_price !== undefined ||
          e.override_quantity !== undefined ||
          e.override_market_value !== undefined,
      ),
    [edits],
  )

  // The per-class rows as the operator sees them (#1631): the compare
  // result's classes with any shares edit applied and, after a row value
  // edit, the Scient NAVs recomputed from the edited totals — the rows the
  // strip renders and the submit echo persists.
  const effectiveClasses = useMemo<ShareClassNavResult[] | null>(
    () =>
      result
        ? effectiveShareClasses(result, shareClassEdits, displayTotals, rowValuesEdited)
        : null,
    [result, shareClassEdits, displayTotals, rowValuesEdited],
  )

  // The NAV controls as the operator sees them: the server's decision for
  // every class an edit left alone, re-evaluated by the same rule where an
  // edit changed a class's figures. The strip's verdict and the submit
  // confirmation's breach list both read this one list; the server
  // re-decides at submit and its result is recorded.
  const navControls = useMemo<NavControlResult[]>(
    () =>
      result ? operatorNavControls(result, effectiveClasses, shareClassEdits, rowValuesEdited) : [],
    [result, effectiveClasses, shareClassEdits, rowValuesEdited],
  )

  // The thresholds the server's controls ran with (CONTEXT.md,
  // "Threshold"), shown wherever a control is read; null when the server
  // ran no controls (unmapped fund).
  const thresholds = useMemo<ControlThresholds | null>(
    () => result?.controls?.thresholds ?? null,
    [result],
  )

  // The rows whose price-gap control did not pass as the operator sees them
  // (displayDiffs, re-evaluated where a price edit changed a row), with the
  // thresholds and the NAV reference the control ran with: the bar counts
  // them, the submit confirmation lists them with the NAV breaches under
  // the one acknowledgement; the server re-decides at submit from the rows
  // sent and the same NAV reference.
  const priceGapBreaches = useMemo<PriceGapBreaches | undefined>(
    () =>
      result?.controls
        ? {
            rows: displayDiffs.filter(isPriceGapBreach),
            thresholds: result.controls.thresholds,
            navReference: result.total_market_value_b,
          }
        : undefined,
    [result, displayDiffs],
  )

  // The share-count breaches still standing: the server's results less the
  // classes accepted, plus any class whose count the operator edited away
  // from the file's figure (shareCountState — the server judges the same
  // edit at submit and refuses it, acceptance or not). While one stands
  // the submit button is closed.
  const shareCountBreaches = useMemo<ShareCountControlResult[]>(
    () =>
      result
        ? unacceptedShareCountBreaches(result.controls, shareCountAcceptances, shareClassEdits)
        : [],
    [result, shareCountAcceptances, shareClassEdits],
  )

  // Submit is available once a compare has run on a mapped fund (Lad,
  // 2026-07-04: submitting a clean day is what records the daily NAV and
  // the snapshots the next day's split depends on) — except while a
  // share-count breach stands unaccepted (spec #1747, the one refusal).
  const canSubmit = result?.account_id != null && shareCountBreaches.length === 0

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      setSelectedFile(file)
      setResult(null)
    }
  }

  const handleCompare = () => {
    if (!selectedFile) return
    compareMutation.mutate(selectedFile)
  }

  const handleCellEdit = useCallback(
    (
      identifier: string,
      field: "price" | "quantity" | "market_value" | "comment",
      value: unknown,
    ) => {
      setEdits(prev => {
        const next = new Map(prev)
        const current: RowOverride = next.get(identifier) ?? {}
        const updated: RowOverride = { ...current }
        if (field === "price") {
          updated.override_price = value === "" || value == null ? undefined : Number(value)
        } else if (field === "quantity") {
          updated.override_quantity = value === "" || value == null ? undefined : Number(value)
        } else if (field === "market_value") {
          updated.override_market_value =
            value === "" || value == null ? undefined : Number(value)
        } else if (field === "comment") {
          updated.comment = value == null ? undefined : String(value)
        }
        const isEmpty =
          updated.override_price === undefined &&
          updated.override_quantity === undefined &&
          updated.override_market_value === undefined &&
          (updated.comment === undefined || updated.comment === "")
        if (isEmpty) next.delete(identifier)
        else next.set(identifier, updated)
        return next
      })
    },
    [],
  )

  const handleShareClassEdit = useCallback(
    (code: string, field: "nombre_parts" | "comment", value: unknown) => {
      setShareClassEdits(prev => {
        const next = new Map(prev)
        const updated: ShareClassEdit = { ...(next.get(code) ?? {}) }
        if (field === "nombre_parts") {
          const raw = String(value ?? "").replace(/,/g, "").trim()
          const parsed = raw === "" ? undefined : Number(raw)
          // Empty clears the edit (revert); garbage or non-positive counts
          // are never a correction — keep the previous state.
          if (parsed === undefined) {
            updated.nombre_parts = undefined
          } else if (Number.isFinite(parsed) && parsed > 0) {
            updated.nombre_parts = parsed
          }
        } else {
          updated.comment = value == null ? undefined : String(value)
        }
        const isEmpty =
          updated.nombre_parts === undefined &&
          (updated.comment === undefined || updated.comment === "")
        if (isEmpty) next.delete(code)
        else next.set(code, updated)
        return next
      })
    },
    [],
  )

  // The accept action in the strip: `null` withdraws the acceptance, an
  // edit object records or re-comments it.
  const handleShareCountAcceptance = useCallback(
    (code: string, acceptance: ShareCountAcceptanceEdit | null) => {
      setShareCountAcceptances(prev => {
        const next = new Map(prev)
        if (acceptance === null) next.delete(code)
        else next.set(code, acceptance)
        return next
      })
    },
    [],
  )

  const handleRevertRow = useCallback((identifier: string) => {
    setEdits(prev => {
      if (!prev.has(identifier)) return prev
      const next = new Map(prev)
      next.delete(identifier)
      return next
    })
  }, [])

  // `acknowledged` is the operator's answer in the submit confirmation
  // (CONTEXT.md, "Acknowledgement"): true only when it listed breaches and
  // they clicked "Acknowledge and submit". The server re-decides the
  // controls from the figures and records the acknowledgement only against
  // breaches it finds.
  const handleSubmit = useCallback(
    (accountId: number, acknowledged: boolean) => {
      if (!result || !selectedFile) return
      const rows: NavSubmissionRow[] = result.diffs.map((baseline, i) =>
        toSubmissionRow(displayDiffs[i], edits.get(baseline.identifier), baseline),
      )
      // Echo the NAVs the operator sees (#1631) — the same rows the summary
      // bar renders, so the persisted figures are the on-screen figures.
      submitMutation.mutate({
        account_id: accountId,
        nav_date: result.nav_date,
        source_file: selectedFile.name,
        source_type: sourceType,
        rows,
        share_class_metadata: result.share_class_metadata ?? undefined,
        share_class_navs:
          effectiveClasses?.map(sc => ({
            code: sc.code,
            nav_per_share_scient: sc.nav_per_share_a,
            nav_per_share_caceis: sc.nav_per_share_b,
          })) ?? undefined,
        share_class_overrides: buildShareClassOverrides(
          result.share_classes,
          shareClassEdits,
        ),
        controls_acknowledged: acknowledged,
        share_count_acceptances: buildShareCountAcceptances(shareCountAcceptances),
        // The NAV reference the price-gap control ran with at compare, so
        // the server decides against the figure the operator acknowledged.
        nav_reference: result.total_market_value_b,
      })
    },
    [
      result,
      selectedFile,
      sourceType,
      displayDiffs,
      edits,
      shareClassEdits,
      shareCountAcceptances,
      effectiveClasses,
      submitMutation,
    ],
  )

  return {
    selectedFile,
    sourceType,
    setSourceType,
    result,
    statusFilter,
    setStatusFilter,
    handleFileChange,
    handleCompare,
    isLoading: compareMutation.isLoading,
    edits,
    effectiveClasses,
    navControls,
    thresholds,
    priceGapBreaches,
    displayDiffs,
    displayTotals,
    correctionHistory,
    handleCellEdit,
    handleShareClassEdit,
    shareClassEdits,
    shareCountAcceptances,
    handleShareCountAcceptance,
    shareCountBreaches,
    canSubmit,
    handleRevertRow,
    handleSubmit,
    isSubmitting: submitMutation.isLoading,
  }
}
